import React, { useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import { scoreRubric } from './questionScoring';

// Section schemas for the structured note formats. Authors set
// question.format to one of 'SOAP' | 'ISBAR' | 'free'.
const FORMAT_SECTIONS = {
  SOAP: [
    { key: 'S', label: 'Subjective', helper: 'What the patient is reporting / context.' },
    { key: 'O', label: 'Objective', helper: 'Vitals, lab results, observed findings.' },
    { key: 'A', label: 'Assessment', helper: 'Your clinical reasoning / what is happening.' },
    { key: 'P', label: 'Plan', helper: 'Specific recommended action(s).' },
  ],
  ISBAR: [
    { key: 'I', label: 'Identify', helper: 'Who you are, who the patient is, why you are calling.' },
    { key: 'S', label: 'Situation', helper: 'The current concern in one sentence.' },
    { key: 'B', label: 'Background', helper: 'Pertinent history, current meds, recent results.' },
    { key: 'A', label: 'Assessment', helper: 'What you think is happening.' },
    { key: 'R', label: 'Recommendation', helper: 'What you want the prescriber to do, by when.' },
  ],
  free: [
    { key: 'text', label: 'Intervention note', helper: 'Free-text recommendation.' },
  ],
};

const InterventionNote = ({ question }) => {
  const format = String(question?.format || 'SOAP').toUpperCase() in FORMAT_SECTIONS
    ? String(question.format).toUpperCase()
    : (question?.format === 'free' ? 'free' : 'SOAP');
  const sections = FORMAT_SECTIONS[format];
  const rubric = useMemo(() => Array.isArray(question?.rubric) ? question.rubric : [], [question]);

  const [values, setValues] = useState(() => sections.reduce((acc, s) => ({ ...acc, [s.key]: '' }), {}));
  const [submitted, setSubmitted] = useState(false);

  const combinedText = useMemo(() => Object.values(values).join('\n\n'), [values]);
  const totalChars = combinedText.replace(/\s+/g, ' ').trim().length;
  const canSubmit = !submitted && totalChars >= 20; // sanity-check minimum

  const scoring = useMemo(() => (submitted ? scoreRubric(combinedText, rubric) : null), [submitted, combinedText, rubric]);

  const handleChange = (key, value) => {
    setValues((curr) => ({ ...curr, [key]: value }));
  };

  return (
    <Container className="mt-3 p-3 bg-light text-dark rounded intervention-note-question">
      <h3 className="d-flex flex-wrap align-items-center gap-2">
        Q{question.questionNumber}: {question.questionTitle}
        {scoring ? (
          <Badge bg={scoring.percentage >= 60 ? 'success' : scoring.percentage >= 40 ? 'warning' : 'danger'} text={scoring.percentage >= 40 && scoring.percentage < 60 ? 'dark' : 'light'}>
            {scoring.percentage}% rubric coverage
          </Badge>
        ) : null}
      </h3>
      <hr />
      <p className="mb-3">{question.questionText}</p>
      <p className="text-muted small mb-3"><strong>Format:</strong> {format === 'free' ? 'Free text' : `${format} structured note`}</p>

      <div className="intervention-note-question__sections">
        {sections.map((s) => (
          <Form.Group key={s.key} className="mb-3" controlId={`int-note-${question.questionNumber}-${s.key}`}>
            <Form.Label className="d-flex justify-content-between align-items-center">
              <span><strong>{s.label}</strong>{format !== 'free' ? <span className="text-muted ms-2">({s.key})</span> : null}</span>
            </Form.Label>
            <Form.Text className="d-block mb-1 text-muted">{s.helper}</Form.Text>
            <Form.Control
              as="textarea"
              rows={s.key === 'P' || s.key === 'R' || s.key === 'text' ? 4 : 3}
              value={values[s.key]}
              disabled={submitted}
              onChange={(e) => handleChange(s.key, e.target.value)}
            />
          </Form.Group>
        ))}
      </div>

      {submitted && scoring ? (
        <div className="intervention-note-question__feedback">
          <h5 className="mt-2">Rubric breakdown</h5>
          <ul className="list-unstyled mb-2">
            {scoring.perCriterion.map((c, idx) => (
              <li key={idx} className="d-flex align-items-start gap-2 mb-1">
                <Badge bg={c.met ? 'success' : 'secondary'}>{c.met ? '✓' : '✗'}</Badge>
                <span>{c.criterion} <span className="text-muted small">(weight {c.weight})</span></span>
              </li>
            ))}
          </ul>
          {question.modelAnswer ? (
            <details className="mt-3">
              <summary><strong>Model answer</strong></summary>
              <div className="mt-2 p-3 bg-white rounded border" style={{ whiteSpace: 'pre-wrap' }}>{question.modelAnswer}</div>
            </details>
          ) : null}
        </div>
      ) : null}

      {!submitted ? (
        <Button
          type="button"
          variant="primary"
          className="mt-3"
          disabled={!canSubmit}
          onClick={() => setSubmitted(true)}
        >
          Submit intervention
        </Button>
      ) : null}
    </Container>
  );
};

export default InterventionNote;
